// @flow
import * as React from 'react';
import { Box } from 'grommet';
import { isEmpty, range, sortBy } from 'lodash';
import memoize from 'memoize-one';
import {
    FormattedMessage, injectIntl,
} from 'react-intl';
import ReactTooltip from 'react-tooltip';
import { VariableSizeList } from 'react-window';
import { withConfirmation } from 'speed-js-react';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import FreeFiberRow from 'esm-web-pro/containers/pages/network/objects/point12/FreeFiberRow';
import DamagedFiberRow from 'esm-web-pro/containers/pages/network/objects/point12/DamagedFiberRow';
import type { SidePanel } from 'esm-web-pro/redux/side-panel/reducer';
import { getSidePanel } from 'esm-web-pro/redux/side-panel';
import { connect } from 'react-redux';
import type { ESMWebProModel } from 'esm-web-pro/types/common';
import CableTitle from 'esm-web-pro/containers/pages/network/objects/point12/CableTitle';
import OpticalPortRow from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/OpticalPortRow';
import ConnectFiber, { CONNECT_FIBER_KEY } from 'esm-web-pro/containers/pages/network/objects/point12/ConnectFiber';
import DisconnectFiber, { DISCONNECT_FIBER_KEY }
    from 'esm-web-pro/containers/pages/network/objects/point12/DisconnectFiber';
import DataPortRow from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/DataPortRow';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import AddSFP from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/AddSFP';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import ContentLabel from 'esm-web-pro/components/layout/sidePanel/ContentLabel';
import type { PermitDocument as PermitDocumentType } from 'esm-web-pro/types/networks';
import PermitDocument from 'esm-web-pro/components/pages/network/PermitDocument';
import SpliceRow from '../SpliceRow';
import { HEIGHTS } from './contants';


export const POINT12_CONTENT_TOOLTIP_KEY = 'point12-content-action-key';

export type SpliceFiberType = {
    id: string,
    uuid: string,
    container: {
        start_container_point: {
            sequence: number,
            id: number,
            coordinates: Object,
        },
        end_container_point: {
            sequence: number,
            id: number,
            coordinates: Object,
        },
        start_termination_point?: {
            sequence: number,
            id: number,
        },
        end_termination_point?: {
            sequence: number,
            id: number,
        },
    },
    sequence_number: number,
};

export type Splice = {
    ...ESMWebProModel,
    sequence: number,
    fiber_starts: Array<SpliceFiberType>,
    fiber_ends: Array<SpliceFiberType>,
    type: string,
};

type Props = {
    point: Object,
    cables: {
        cables: Object,
        more: ?boolean,
    },
    splices: {
        splices: Object,
        more: ?boolean,
    },
    editor: BaseEditor,
    map: Object,
    onView: Function,
    getCables: Function,
    clickedRowIdentity: ?string,
    setClickedRowIdentity: Function,
    highlightFiber: Function,
    highlightFiberCleanUp: Function,
    clearHighlightedFibers: Function,
    disconnectPanel: SidePanel,
    connectPanel: SidePanel,
    opticalODFPorts: {
        opticalODFPorts: Object,
        more: ?boolean,
    },
    opticalSwitchPorts: Object[],
    dataPorts: Object[],
    reload: Function,
};

type State = {
    connecting: ?Object,
    disconnecting: ?Object,
    groupedFibersForSplicing: Object,
    groupedFibersForSplicingLength: number,
    addingSFP?: Object,
};


/**
 * @param {{}} freeFibers Fibers object
 * @param {string | number} sequence Fiber sequence number
 * Groups the fibers by cables where each fiber points to the number of sequence fibers
 * @example
 * // for free fibers 1, 2, 4, 5, 6 in cable 5
 * // return {
 *     5: {
 *         1: 5
 *         2: 4
 *         4: 3
 *         5: 2
 *         6: 1
 *     }
 * }
 * @return {void} Stores the object in the component state
 */
const markAsFree = memoize((freeFibers, sequence) => {
    if (freeFibers && freeFibers[sequence]) return;

    if (!freeFibers) {
        // eslint-disable-next-line no-param-reassign
        freeFibers = {};
    }
    freeFibers[sequence] = 1;
    range(1, sequence).reverse().every((currFiber) => {
        if (freeFibers[currFiber]) {
            freeFibers[currFiber] += 1;
        }
        return freeFibers[currFiber];
    });
});


const getFreeFibersForSplicing = memoize((cables: Object) => {
    const freeFibers = {};
    Object.keys(cables).forEach((cableId) => {
        if (!cables[cableId].free_fibers) return;

        freeFibers[cableId] = {};
        cables[cableId].free_fibers.forEach((fiber) => {
            markAsFree(freeFibers[cableId], fiber.sequence_number);
        });
    });


    return freeFibers;
});

const getTitle = (type) => {
    switch (type) {
    case 'coupler':
        return (
            <FormattedMessage
                id="point12-content-splices"
                defaultMessage="Splices"
            />
        );
    case 'switch':
        return (
            <FormattedMessage
                id="switch-content-title"
                defaultMessage="Data ports"
            />
        );
    case 'sfp':
        return null;
    default:
        return (
            <FormattedMessage
                id="point12-content-connected"
                defaultMessage="Connected"
            />
        );
    }
};

const MIN_100_PC = { min: '100%' };

const mapStateToProps = (state) => ({
    disconnectPanel: (() => getSidePanel(state, DISCONNECT_FIBER_KEY))(),
    connectPanel: (() => getSidePanel(state, CONNECT_FIBER_KEY))(),
});

@connect(mapStateToProps)
@injectIntl
@withConfirmation
class Point12CableList extends React.PureComponent<Props, State> {
    listRef: React.Ref;

    static defaultProps = {
        disconnectPanel: undefined,
        connectPanel: undefined,
    };

    static getDerivedStateFromProps(props: Props, state: State) {
        const { cables: { cables } } = props;
        if (cables) {
            const groupedFibersForSplicing = getFreeFibersForSplicing(cables);

            if (state.groupedFibersForSplicing !== groupedFibersForSplicing) {
                return {
                    groupedFibersForSplicing,
                    groupedFibersForSplicingLength: Object.keys(groupedFibersForSplicing).length,
                };
            }
        }

        return null;
    }

    state = {
        connecting: null,
        disconnecting: null,
        groupedFibersForSplicing: {},
        groupedFibersForSplicingLength: 0,
    };

    constructor(props: Props) {
        super(props);

        this.listRef = React.createRef();
        this.getListData = memoize(this.getListData);
        this.getRowSize = memoize(this.getRowSize);
        this.getCables = memoize(this.getCables);
        this.getPointCoordinates = memoize(this.getPointCoordinates);
    }

    componentDidUpdate(prevProps: Props, prevState: State): void {
        const {
            disconnectPanel: prevDisconnectPanel, connectPanel: prevConnectPanel,
            splices: prevSplices, cables: prevCables, opticalODFPorts: prevOpticalODFPorts,
        } = prevProps;
        const connecting = prevProps.cables?.cables?.connecting;
        const {
            onView, disconnectPanel, connectPanel, editor, splices, cables, opticalODFPorts,
        } = this.props;

        const { connecting: prevConnecting } = prevState;

        if (prevConnecting && connecting === null) {
            onView();
        }

        if (prevDisconnectPanel && !disconnectPanel) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ disconnecting: null });
            editor.unregisterAllLoadingCallback();
        }

        if (prevConnectPanel && !connectPanel) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ connecting: null });
        }

        if (this.listRef.current && (
            prevSplices !== splices
            || prevCables.cables !== cables.cables
            || prevOpticalODFPorts !== opticalODFPorts
        )
        ) {
            this.listRef.current.resetAfterIndex(0);
        }

        ReactTooltip.rebuild();
    }

    handleOpenDisconnectForm = (object: Object) => {
        this.setState({ disconnecting: object });
    };

    handleConnect = (data: Object) => {
        this.setState({ connecting: data });
    };

    handleAddSFP = (dataPort: Object) => {
        this.setState({ addingSFP: dataPort });
    };

    reloadContent = () => {
        const { reload } = this.props;
        reload();
    };

    getFiberCableSeq = (cable) => {
        const { point } = this.props;
        const {
            start_termination_point: cableStartPoint,
            start_sequence: startSequence, end_sequence: endSequence,
            patch, patch_sequence: patchSequence,
        } = cable;

        if (patch) {
            return patchSequence;
        }
        return cableStartPoint && cableStartPoint.id === point.id ? startSequence : endSequence;
    };

    getCables = (cables: Object) => (
        Object.keys(cables).map((cableId) => ({
            id: cableId,
            uuid: cables[cableId].uuid,
            path: cables[cableId].path,
            sequence: cables[cableId].sequence,
            reserve: cables[cableId].reserve,
            fiber_count: cables[cableId].fiber_count,
            free_fibers: cables[cableId].free_fibers,
            patch: cables[cableId].patch,
        }))
    );

    getPointCoordinates = (point: Object) => {
        if (point.type === 'sfp') {
            return [...point.container.container.coordinates.coordinates].reverse();
        }
        return [...point.container.coordinates.coordinates].reverse();
    };

    getListData = (
        cables: ?{
        cables: Object, more: boolean,
    }, splices: ?{
        splices: Object[],
        more: boolean,
    }, opticalODFPorts: ?{
        opticalODFPorts: Object[],
        more: boolean,
    }, opticalSwitchPorts: ?Object[], dataPorts: ?Object[], permitDocuments: ?PermitDocumentType[],
    ) => {
        const { point } = this.props;

        const result = point.type !== 'sfp' ? [{
            type: 'padding',
            data: '20px',
        }, {
            type: 'title',
            data: getTitle(point.type),
        }] : [];

        if (point.type === 'coupler' || point.type === 'terminalbox') {
            if (!splices.more && isEmpty(splices?.splices)) {
                result.push({
                    type: 'empty',
                    data: (
                        <FormattedMessage
                            id="point12-no-content-connected"
                            defaultMessage="No connected fibers"
                        />
                    ),
                });
            } else {
                splices.splices.forEach((s) => {
                    result.push({
                        type: 'splice',
                        data: s,
                    });
                });
            }

            if (splices.more) {
                result.push({
                    type: 'loading',
                    data: null,
                });
            }
        } else if (point.type === 'odf' && !isEmpty(opticalODFPorts?.opticalODFPorts)) {
            opticalODFPorts.opticalODFPorts.forEach((p) => {
                result.push({
                    type: 'odfport',
                    data: p,
                });
            });
        } else if (point.type === 'switch' && dataPorts) {
            dataPorts.forEach((p) => {
                result.push({
                    type: 'dataport',
                    data: p,
                });
            });
        } else if (point.type === 'sfp') {
            result.push({
                type: 'sfp',
                data: point,
            });
        }

        if ((point.type === 'odf' && opticalODFPorts.more)
        || (point.type === 'switch' && !dataPorts)) {
            result.push({
                type: 'loading',
                data: null,
            });
        } else if (point.type === 'sfp' && opticalSwitchPorts) {
            opticalSwitchPorts.forEach((p) => {
                result.push({
                    type: 'switchport',
                    data: p,
                });
            });
        }

        if (point.type !== 'switch' && point.type !== 'sfp') {
            result.push({
                type: 'title',
                data: (
                    <FormattedMessage
                        id="point12-content-disconnected"
                        defaultMessage="Disconnected"
                    />
                ),
            });

            let hasDisconnected = false;
            sortBy(cables.cables, ['sequence']).forEach((cable) => {
                if (!isEmpty(cable.free_fibers)) {
                    result.push({
                        type: 'cable',
                        data: cable,
                    });
                    cable.free_fibers.forEach((ff) => {
                        result.push({
                            type: 'freeFiber',
                            data: {
                                cable,
                                fiber: ff,
                            },
                        });
                    });
                    hasDisconnected = true;
                }
            });

            if (cables.more !== false) {
                result.push({
                    type: 'loading',
                    data: null,
                });
            } else if (!hasDisconnected) {
                result.push({
                    type: 'empty',
                    data: (
                        <FormattedMessage
                            id="point12-no-content-disconnected"
                            defaultMessage="No disconnected fibers"
                        />
                    ),
                });
            }

            result.push({
                type: 'title',
                data: (
                    <FormattedMessage
                        id="point12-content-damaged"
                        defaultMessage="Damaged"
                    />
                ),
            });

            let hasDamaged = false;
            if (cables.cables) {
                Object.keys(cables.cables).forEach((key) => {
                    const cable = cables.cables[key];

                    if (!isEmpty(cable.damaged_fibers)) {
                        result.push({
                            type: 'cable',
                            data: cable,
                        });
                        cable.damaged_fibers.forEach((df) => {
                            result.push({
                                type: 'damagedFiber',
                                data: {
                                    cable,
                                    fiber: df,
                                    cableId: key,
                                },
                            });
                        });
                        hasDamaged = true;
                    }
                });
            }

            if (cables.more !== false) {
                result.push({
                    type: 'loading',
                    data: null,
                });
            } else if (!hasDamaged) {
                result.push({
                    type: 'empty',
                    data: (
                        <FormattedMessage
                            id="point12-no-content-damaged"
                            defaultMessage="No damaged fibers"
                        />
                    ),
                });
            }
        }

        result.push({
            type: 'title',
            data: (
                <FormattedMessage
                    id="permit-documents"
                    defaultMessage="Permit Documents"
                />
            ),
        });
        if (!isEmpty(permitDocuments)) {
            permitDocuments.forEach((permitDocument) => {
                result.push({
                    type: 'document',
                    data: permitDocument,
                });
            });
        } else {
            result.push({
                type: 'empty',
                data: (
                    <FormattedMessage
                        id="no-permit-documents"
                        defaultMessage="No permit documents"
                    />
                ),
            });
        }

        result.push({
            type: 'padding',
            data: '20px',
        });

        return result;
    };

    renderRow = (
        clickedRowIdentity, {
            index, data: dataArray, style,
        }: {
            index: number, data: Array<{ type: string, data: mixed }>, style: Object
        },
    ) => {
        const {
            point, cables: { cables, more: moreCables }, onView, map, editor, setClickedRowIdentity, highlightFiber,
            splices, opticalODFPorts,
        } = this.props;

        const { data, type } = dataArray[index];

        const { groupedFibersForSplicingLength } = this.state;

        const shouldDisableSplicing = groupedFibersForSplicingLength < 2;
        const shouldShowSpliceIconOnFreeFiberRow = point.type !== 'odf';
        const disableSpliceBreak = splices?.more || false;
        const disablePortDisconnect = opticalODFPorts?.more || false;

        let result;

        switch (type) {
        case 'loading':
            result = (
                // $FlowFixMe;
                <Spinner
                    type="clipLoader"
                    loading
                    blocking={false}
                    color="black"
                    coverStyle={{
                        position: 'relative',
                        width: 'auto',
                        height: '100%',
                    }}
                />
            );
            break;
        case 'title':
            result = (
                <Box pad={{ top: 'xsmall' }}>
                    <ContentLabel>
                        {data}
                    </ContentLabel>
                </Box>
            );
            break;
        case 'padding':
            result = (
                <div style={{ height: data }} />
            );
            break;
        case 'empty':
            result = (
                <Box pad={{ bottom: 'xsmall' }}>
                    <ContentRow padLevel={1} hoverIndicator="none">
                        <ValueText>
                            {data}
                        </ValueText>
                    </ContentRow>
                </Box>
            );
            break;
        case 'splice':
            result = (
                <SpliceRow
                    key={`s-${data.uuid}`}
                    pointCoords={this.getPointCoordinates(point)}
                    splice={data}
                    openBreakSpliceForm={this.handleOpenDisconnectForm}
                    map={map}
                    editor={editor}
                    onView={onView}
                    clickedRowIdentity={clickedRowIdentity}
                    setClickedRowIdentity={setClickedRowIdentity}
                    highlightFiber={highlightFiber}
                    getFiberCableSeq={this.getFiberCableSeq}
                    disableSpliceBreak={disableSpliceBreak}
                    loading={splices?.more}
                />
            );
            break;
        case 'odfport':
            result = (
                <OpticalPortRow
                    key={`p-${data.uuid}`}
                    opticalPort={data}
                    clickedRowIdentity={clickedRowIdentity}
                    setClickedRowIdentity={setClickedRowIdentity}
                    getFiberCableSeq={this.getFiberCableSeq}
                    editor={editor}
                    map={map}
                    onConnect={this.handleConnect}
                    onDisconnect={this.handleOpenDisconnectForm}
                    onView={onView}
                    pointCoords={this.getPointCoordinates(point)}
                    point={point}
                    cables={cables}
                    disablePortDisconnect={disablePortDisconnect}
                    fiberLoading={moreCables !== false}
                    portLoading={opticalODFPorts?.more}
                />
            );
            break;
        case 'switchport':
            result = (
                <OpticalPortRow
                    key={`p-${data.uuid}`}
                    switchPortIndex={index}
                    opticalPort={data}
                    dataArray={dataArray}
                    clickedRowIdentity={clickedRowIdentity}
                    setClickedRowIdentity={setClickedRowIdentity}
                    getFiberCableSeq={this.getFiberCableSeq}
                    editor={editor}
                    map={map}
                    onConnect={this.handleConnect}
                    onDisconnect={this.handleOpenDisconnectForm}
                    onView={onView}
                    pointCoords={this.getPointCoordinates(point)}
                    point={point}
                    cables={cables}
                />
            );
            break;
        case 'dataport':
            result = (
                <DataPortRow
                    point={point}
                    key={`p-${data.uuid}`}
                    port={data}
                    onAddSFP={this.handleAddSFP}
                    onRemoveSFP={this.reloadContent}
                    onPortConnect={this.handleConnect}
                    onPortDisconnect={this.handleOpenDisconnectForm}
                />
            );
            break;
        case 'cable':
            result = (
                <Box pad={{ top: 'xxsmall' }}>
                    <CableTitle
                        rowId={`cable-${data.id}-free-fibers`}
                        cable={data}
                        editor={editor}
                        setClickedRowIdentity={setClickedRowIdentity}
                        clickedRowIdentity={clickedRowIdentity}
                    />
                </Box>
            );
            break;
        case 'freeFiber':
            result = (
                <FreeFiberRow
                    key={`${data.cable.id}-${data.fiber.sequence_number}`}
                    cable={{ id: data.cable.id, ...data.cable }}
                    fiber={data.fiber}
                    fiberSequence={data.fiber.sequence_number}
                    onConnect={this.handleConnect}
                    onDamage={this.reloadContent}
                    map={map}
                    onView={onView}
                    clickedRowIdentity={clickedRowIdentity}
                    setClickedRowIdentity={setClickedRowIdentity}
                    highlightFiber={highlightFiber}
                    pointCoords={this.getPointCoordinates(point)}
                    shouldDisableSplicing={shouldDisableSplicing}
                    shouldShowSpliceIconOnFreeFiberRow={shouldShowSpliceIconOnFreeFiberRow}
                />
            );
            break;
        case 'damagedFiber':
            result = (
                <DamagedFiberRow
                    cable={{ id: data.cable, ...data.cable }}
                    fiber={data.fiber}
                    onFix={this.reloadContent}
                    key={`damagedfiber-${data.fiber.sequence_number}`}
                    map={map}
                    onView={onView}
                    clickedRowIdentity={clickedRowIdentity}
                    setClickedRowIdentity={setClickedRowIdentity}
                    highlightFiber={highlightFiber}
                    pointCoords={this.getPointCoordinates(point)}
                />
            );
            break;
        case 'document':
            result = (
                <PermitDocument document={data} reload={this.reloadContent} />
            );
            break;
        default:
            result = null;
        }

        return (
            <div style={{ ...style, display: 'flex', flexDirection: 'column' }}>
                {result}
            </div>
        );
    };

    getRowSize = (data: Array<{ type: string, ... }>) => (index: number): number => {
        if (typeof HEIGHTS[data[index].type] === 'function') {
            return HEIGHTS[data[index].type](data[index]);
        }
        return HEIGHTS[data[index].type];
    };

    render() {
        const {
            point, editor, cables, getCables, splices, highlightFiber, highlightFiberCleanUp,
            clearHighlightedFibers, opticalODFPorts, reload, clickedRowIdentity, dataPorts, opticalSwitchPorts,
        } = this.props;

        const {
            connecting, disconnecting, groupedFibersForSplicing, addingSFP,
        } = this.state;

        const { permit_documents: permitDocuments } = point;

        const container = point.type === 'sfp' ? point.container : point;

        if (connecting) {
            return (
                <ConnectFiber
                    connecting={connecting}
                    container={container}
                    cables={this.getCables(cables.cables)}
                    opticalPorts={opticalODFPorts?.opticalODFPorts || opticalSwitchPorts}
                    highlightFiber={highlightFiber}
                    highlightFiberCleanUp={highlightFiberCleanUp}
                    clearHighlightedFibers={clearHighlightedFibers}
                    freeFibers={groupedFibersForSplicing}
                    editor={editor}
                    onClose={() => this.setState({ connecting: null })}
                    onSave={() => {
                        this.reloadContent();
                    }}
                />
            );
        }

        if (disconnecting) {
            return (
                <DisconnectFiber
                    disconnecting={disconnecting}
                    splices={splices?.splices}
                    getFiberCableSeq={this.getFiberCableSeq}
                    onBreak={() => {
                        this.reloadContent();
                    }}
                    opticalPorts={opticalODFPorts?.opticalODFPorts || opticalSwitchPorts}
                />
            );
        }

        if (addingSFP) {
            return (
                <AddSFP
                    port={addingSFP}
                    onClose={() => this.setState({ addingSFP: null }, this.reloadContent)}
                    onSave={() => this.setState({ addingSFP: null }, this.reloadContent)}
                    openInitially
                />
            );
        }

        const data = this.getListData(cables, splices, opticalODFPorts, opticalSwitchPorts, dataPorts, permitDocuments);

        const height = window.innerHeight
            - 60 // Page header
            - 70 // Side panel title
            - (286 // Attributes and actions
                + (point.numbering_scheme ? HEIGHTS.attributes.numberingScheme : 0)
                + (point.u_slot_number ? HEIGHTS.attributes.uSlotNumber : 0)
                + (point.type === 'switch' ? 57 : 0)
            )
            - 56; // Close button

        return (
            <Box height={MIN_100_PC}>
                <VariableSizeList
                    ref={this.listRef}
                    height={height}
                    width="100%"
                    itemCount={data.length}
                    itemSize={this.getRowSize(data)}
                    itemData={data}
                    onItemsRendered={ReactTooltip.rebuild}
                >
                    {this.renderRow.bind(this, clickedRowIdentity)}
                </VariableSizeList>
                <ReactTooltip
                    id={POINT12_CONTENT_TOOLTIP_KEY}
                    effect="solid"
                    place="top"
                    className="react-tooltip"
                />
            </Box>
        );
    }
}

export default Point12CableList;
